import { getEntityTypeLabelWithFallback, getEntityTypePluralLabelWithFallback, } from "./framework/entity-labels.js";
import { capitalizeWords } from "./strings.js";
function normalizeUserFacingLabel(label) {
    return label.trim().replace(/\s+/g, " ").toLowerCase();
}
function materializeEntityTargetTypeDefinition(entityTypeKey, structure) {
    return {
        targetKey: entityTypeKey,
        targetType: "entity",
        defaultSubjectPresentation: {
            singularLabel: normalizeUserFacingLabel(getEntityTypeLabelWithFallback(structure, entityTypeKey)),
            pluralLabel: normalizeUserFacingLabel(getEntityTypePluralLabelWithFallback(structure, entityTypeKey)),
            namedCreatePrefix: null,
        },
        intrinsicParentContext: {},
    };
}
const BUILT_IN_TARGET_TYPE_DEFINITIONS = [
    {
        targetKey: "projectTreeFolder",
        targetType: "nonEntity",
        defaultSubjectPresentation: {
            singularLabel: "folder",
            pluralLabel: "folders",
            namedCreatePrefix: "folder",
        },
        intrinsicParentContext: {
            metadataKey: "projectTreeFolderSlug",
        },
    },
];
function materializeCompiledTargetDefinition(args) {
    const singularLabel = normalizeUserFacingLabel(args.singularLabel ?? args.targetTypeDefinition.defaultSubjectPresentation.singularLabel);
    const pluralLabel = normalizeUserFacingLabel(args.pluralLabel ?? args.targetTypeDefinition.defaultSubjectPresentation.pluralLabel);
    return {
        targetKey: args.targetTypeDefinition.targetKey,
        targetType: args.targetTypeDefinition.targetType,
        subjectPresentation: {
            singularLabel,
            pluralLabel,
            titleCaseLabel: capitalizeWords(singularLabel),
            fallbackReferenceLabel: `the ${singularLabel}`,
            namedCreatePrefix: args.targetTypeDefinition.defaultSubjectPresentation.namedCreatePrefix,
        },
        routeContextResolver: args.routeContextResolver ?? null,
    };
}
export function listFrameworkTargetTypeDefinitions() {
    return [...BUILT_IN_TARGET_TYPE_DEFINITIONS];
}
export function getFrameworkTargetTypeDefinition(targetKey) {
    const builtInDefinition = BUILT_IN_TARGET_TYPE_DEFINITIONS.find((definition) => definition.targetKey === targetKey);
    if (builtInDefinition) {
        return builtInDefinition;
    }
    return materializeEntityTargetTypeDefinition(targetKey);
}
export function buildCompiledFrameworkTargetDefinitions(args) {
    const targets = new Map();
    for (const display of args.entityDisplays) {
        targets.set(display.entityTypeKey, materializeCompiledTargetDefinition({
            targetTypeDefinition: {
                targetKey: display.entityTypeKey,
                targetType: "entity",
                defaultSubjectPresentation: {
                    singularLabel: display.label,
                    pluralLabel: display.pluralLabel,
                    namedCreatePrefix: null,
                },
                intrinsicParentContext: {},
            },
            singularLabel: display.label,
            pluralLabel: display.pluralLabel,
        }));
    }
    if (args.folderNodes.some((node) => node.kind === "root" && node.settings?.projectTreeFolders?.enabled === true)) {
        targets.set("projectTreeFolder", materializeCompiledTargetDefinition({
            targetTypeDefinition: getFrameworkTargetTypeDefinition("projectTreeFolder"),
            routeContextResolver: {
                source: "currentProjectTreeFolder",
            },
        }));
    }
    return Array.from(targets.values());
}
export function getCompiledFrameworkTargetDefinition(structure, targetKey) {
    return structure?.targets.find((target) => target.targetKey === targetKey) ?? null;
}
export function requireCompiledFrameworkTargetDefinition(args) {
    const compiled = getCompiledFrameworkTargetDefinition(args.structure, args.targetKey);
    if (compiled) {
        return compiled;
    }
    throw new Error(`Compiled framework target is missing: ${args.targetKey}`);
}
export function getFrameworkTargetSubjectPresentation(args) {
    return requireCompiledFrameworkTargetDefinition(args).subjectPresentation;
}
export function getCompiledFrameworkTargetContextLabel(args) {
    return getFrameworkTargetSubjectPresentation(args).titleCaseLabel;
}
export function getFrameworkTargetContextMetadataKey(args) {
    return (getFrameworkTargetTypeDefinition(args.targetKey).intrinsicParentContext.metadataKey ??
        args.bindingPersistenceKey.replace(/Id$/, "Slug"));
}
export function getFrameworkTargetRouteContextResolver(args) {
    return requireCompiledFrameworkTargetDefinition(args).routeContextResolver ?? null;
}
export function getCompiledProjectTreeFolderManagedPlacement(args) {
    const rootNode = args.structure?.folderNodes.find((node) => node.kind === "root" &&
        (node.rootFolderKey === args.rootFolderKeyOrSlug ||
            node.rootFolderSlug === args.rootFolderKeyOrSlug)) ?? null;
    if (!rootNode) {
        return null;
    }
    return (rootNode.settings?.projectTreeFolders?.managedPlacements?.find((placement) => placement.targetEntityTypeKey === args.targetEntityTypeKey) ?? null);
}
